import type {NotifiableError} from '@bugsnag/js';

// helpers is the only blocking bundle in <head>, so importing this first makes
// it the earliest listener on the page. Bugsnag starts later, from app.js.

// Bounded so a loop that throws every iteration cannot retain unboundedly.
const MAX_BUFFERED = 10;

const buffered: NotifiableError[] = [];

const record = (error: NotifiableError) => {
    if (buffered.length < MAX_BUFFERED) {
        buffered.push(error);
    }
};

// Bugsnag's own onerror drops these, and buffering the bare message would
// report a stack at the drain site rather than the throw.
const isCrossOriginError = (event: ErrorEvent) =>
    event.lineno === 0 && /Script error\.?/.test(event.message);

const onError = (event: ErrorEvent) => {
    if (!event.error && isCrossOriginError(event)) {
        return;
    }

    record(event.error ?? event.message);
};

const onRejection = (event: PromiseRejectionEvent) => record(event.reason);

const stopListening = () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
};

window.addEventListener('error', onError);
window.addEventListener('unhandledrejection', onRejection);

/**
 * Must be called synchronously after Bugsnag.start(), or an error landing
 * between the two handlers is reported twice.
 */
export const drainEarlyErrors = (): NotifiableError[] => {
    stopListening();

    return buffered.splice(0, buffered.length);
};
